import { Activity, AlertCircle, Info, User } from "lucide-react";
import type React from "react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMedicalReports } from "@/features/imagings/hooks/useMedicalReports";
import type { Imaging } from "@/features/imagings/schemas/imagings";

interface StudyInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  imaging: Imaging;
}

export function StudyInfoDialog({
  isOpen,
  onClose,
  imaging,
}: StudyInfoDialogProps): React.JSX.Element {
  const { data: reports } = useMedicalReports(
    imaging.uid,
    imaging.accession_number || "",
    imaging.institution_id || "",
    isOpen
  );

  const risReport = reports?.ris && reports.ris.length > 0 ? reports.ris[0] : null;

  /**
   * Formatea la fecha para mostrar de forma segura
   */
  const formatDate = (date: any) => {
    try {
      if (!date) return "—";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }).format(d);
    } catch {
      return "—";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90dvh] h-auto flex flex-col p-0 overflow-hidden bg-slate-50 dark:bg-slate-950 border-none shadow-2xl">
        <DialogHeader className="p-4 sm:p-6 pb-4 flex flex-row items-center gap-4 space-y-0 relative border-b border-slate-100 dark:border-slate-800 shrink-0">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shrink-0">
            <Info className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-0.5 text-left flex-1 min-w-0">
            <DialogTitle className="text-xl font-bold m-0 p-0 text-slate-900 dark:text-slate-50">
              Detalles del Registro
            </DialogTitle>
            <p className="text-xs text-muted-foreground truncate w-full uppercase tracking-wider font-medium">
              {imaging.modality} • {imaging.patient_name.replace(/\^/g, " ").trim()}
            </p>
          </div>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <div className="px-6 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950/50">
            <TabsList className="bg-transparent h-auto p-0 gap-6">
              <TabsTrigger
                value="general"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-0 py-3 font-bold text-xs uppercase tracking-widest text-slate-500 data-[state=active]:text-blue-600"
              >
                Información General
              </TabsTrigger>
              <TabsTrigger
                value="ris"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent px-0 py-3 font-bold text-xs uppercase tracking-widest text-slate-500 data-[state=active]:text-blue-600"
              >
                Integración RIS
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 p-4 sm:p-6 overflow-y-auto custom-scrollbar">
            <TabsContent
              value="general"
              className="m-0 focus-visible:outline-none h-full"
            >
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    N° Acceso
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {imaging.accession_number || "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    Fecha Proceso
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {formatDate(imaging.datetime)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    Institución
                  </p>
                  <p className="font-semibold truncate text-slate-900 dark:text-slate-100">
                    {imaging.institution_name || "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    ID Paciente
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {imaging.patient_id || "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    Médico Referidor
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {imaging.referring_physician
                      ? imaging.referring_physician.replace(/\^/g, " ").trim()
                      : "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    Imágenes
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {imaging.number_imagings || 0}
                  </p>
                </div>
                <div className="col-span-2 space-y-1">
                  <p className="text-muted-foreground text-[10px] uppercase font-bold tracking-wider">
                    Descripción del Estudio
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {imaging.description || "—"}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ris" className="m-0 focus-visible:outline-none h-full">
              {!risReport ? (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-3 opacity-60">
                  <AlertCircle className="h-10 w-10 text-slate-400" />
                  <div>
                    <p className="text-sm font-bold text-slate-600 dark:text-slate-400">
                      Sin datos de integración
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">
                      No se encontró un registro RIS para este estudio
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Paciente Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                      <User className="h-4 w-4 text-blue-500" />
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-300">
                        Información del Paciente
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Nombre Completo
                        </p>
                        <p className="font-semibold">{risReport.patient_name || "—"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Identificación
                        </p>
                        <p className="font-semibold">
                          {risReport.patient_identification || "—"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          F. Nacimiento
                        </p>
                        <p className="font-semibold">{risReport.patient_birth || "—"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Género
                        </p>
                        <p className="font-semibold">
                          {risReport.patient_gender === "F"
                            ? "Femenino"
                            : risReport.patient_gender === "M"
                              ? "Masculino"
                              : risReport.patient_gender || "—"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Email
                        </p>
                        <p className="font-semibold truncate">
                          {risReport.patient_email || "—"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Teléfono
                        </p>
                        <p className="font-semibold">{risReport.patient_phone || "—"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Estudio RIS Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                      <Activity className="h-4 w-4 text-teal-500" />
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-300">
                        Detalles del Informe / Estudio
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Médico Informante
                        </p>
                        <p className="font-bold text-blue-600 dark:text-blue-400">
                          {risReport.physician_name || "—"}
                        </p>
                        {risReport.physician_specialty && (
                          <p className="text-[10px] text-slate-500 italic">
                            {risReport.physician_specialty}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Estado del Informe
                        </p>
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-2 w-2 rounded-full ${risReport.has_report ? "bg-green-500" : "bg-amber-500"}`}
                          />
                          <p className="font-semibold">
                            {risReport.has_report ? "Disponible" : "Sin Informe"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Nombre del Estudio
                        </p>
                        <p className="font-semibold">{risReport.study_name || "—"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Tipo de Estudio
                        </p>
                        <p className="font-semibold capitalize">
                          {risReport.study_type || "—"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Fecha Reporte
                        </p>
                        <p className="font-semibold">
                          {risReport.date_report
                            ? new Date(risReport.date_report).toLocaleString("es-ES")
                            : "—"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-[10px] uppercase font-bold">
                          Institución Referida
                        </p>
                        <p className="font-semibold truncate">
                          {risReport.referred_institution || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
